import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import './AuthCallback.css'; // Reusing the same styles

function AuthConfirm() {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleConfirmation = async () => {
      try {
        console.log('Current path:', location.pathname);
        console.log('Search params:', location.search);
        
        // Get parameters from URL - handle both query params and path segments
        let tokenHash = searchParams.get('token_hash');
        let type = searchParams.get('type') || 'recovery';
        let redirectUrl = searchParams.get('redirectUrl');
        
        // Handle malformed URL where redirectUrl is part of the path
        if (!tokenHash && location.pathname.includes('/auth/confirm/')) {
          // Try to extract parameters from the pathname
          const pathParts = location.pathname.split('/auth/confirm/');
          if (pathParts.length > 1) {
            // The rest of the path might be the redirectUrl
            redirectUrl = pathParts[1];
            
            // Get token from query string if available
            if (location.search) {
              const queryParams = new URLSearchParams(location.search);
              tokenHash = queryParams.get('token_hash');
              type = queryParams.get('type') || type;
            }
          }
        }
        
        // If no token found and we have a hash in the URL, it might contain the parameters
        if (!tokenHash && window.location.hash) {
          try {
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            tokenHash = hashParams.get('token_hash');
            type = hashParams.get('type') || type;
            redirectUrl = hashParams.get('redirectUrl') || redirectUrl;
          } catch (e) {
            console.warn('Failed to parse hash params:', e);
          }
        }
        
        // Basic validation
        if (!tokenHash) {
          setError('No token found in URL. Please check your reset link.');
          setIsLoading(false);
          return;
        }

        console.log('Processing token confirmation:', tokenHash);
        console.log('Type:', type);
        
        // Normalize redirect URL - Make sure we extract just the path if it's a full URL
        let targetUrl = '/update-password'; // Default redirect
        if (redirectUrl) {
          console.log('Original redirectUrl:', redirectUrl);
          // Check if it's a full URL
          try {
            // If it's a full URL, extract just the path
            if (redirectUrl.startsWith('http')) {
              const urlObj = new URL(redirectUrl);
              targetUrl = urlObj.pathname;
            } else {
              // If it's just a path, use it directly
              targetUrl = redirectUrl;
            }
          } catch (e) {
            console.warn('Failed to parse redirect URL:', e);
            // Fall back to default
            targetUrl = '/update-password';
          }
        }
        
        console.log('Will redirect to:', targetUrl);

        // Verify the token with Supabase
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: type,
        });

        if (error) {
          console.error('Token verification failed:', error);
          setError(error.message || 'Failed to verify token');
          setIsLoading(false);
          return;
        }

        console.log('Token verified successfully', data);
        
        // Get the current session after verification
        const { data: sessionData } = await supabase.auth.getSession();
        console.log('Session after verification:', sessionData?.session?.access_token);
        
        // Store a flag in localStorage to indicate successful auth
        localStorage.setItem('auth_verified', 'true');
        
        // Add a timestamp to force a cache refresh
        const timestamp = new Date().getTime();
        
        console.log('Redirecting to:', targetUrl);
        
        // Redirect with timestamp to avoid caching issues
        window.location.href = `${targetUrl}?auth_success=${timestamp}`;
      } catch (err) {
        console.error('Error during token confirmation:', err);
        setError('An unexpected error occurred');
        setIsLoading(false);
      }
    };

    handleConfirmation();
  }, [searchParams, navigate, location]);

  if (isLoading) {
    return (
      <div className="auth-callback-container">
        <div className="auth-callback-card loading">
          <div className="loader"></div>
          <p>Verifying your request...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auth-callback-container">
        <div className="auth-callback-card error">
          <div className="error-icon">!</div>
          <h1>Error</h1>
          <p>{error}</p>
          <a href="/" className="dashboard-button">
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-callback-container">
      <div className="auth-callback-card loading">
        <div className="loader"></div>
        <p>Redirecting...</p>
      </div>
    </div>
  );
}

export default AuthConfirm; 